import { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AppIconButton, Sheet } from '@clickguard/design-system'
import { XClose } from 'components/icons'
import { FadingScrollArea } from 'components/fading-scroll/fading-scroll-area'
import './connections-sheet.css'

/** Layout adapter for the DS Sheet; the DS still owns dismissal and motion. */
export const ConnectionsSheet = ({
	open,
	onClose,
	title,
	tabs,
	footer,
	plain = false,
	closeOnEscape = true,
	children
}: {
	open: boolean
	onClose: () => void
	title: string
	tabs?: ReactNode
	footer: ReactNode
	plain?: boolean
	closeOnEscape?: boolean
	children: ReactNode
}) => {
	useEffect(() => {
		if (!open) return
		document.body.classList.add('connections-sheet-open')
		return () => document.body.classList.remove('connections-sheet-open')
	}, [open])

	return createPortal(
		<Sheet
			open={open}
			onClose={onClose}
			width={460}
			hideCloseButton
			closeOnEscape={closeOnEscape}
			aria-label={title}
			className="connections-sheet"
			overlayClassName="connections-sheet-overlay"
		>
			<header className="shrink-0 border-b border-[rgb(var(--cg-border))]">
				<div
					className={`flex items-start justify-between gap-4 px-6 pt-6 ${
						tabs ? 'pb-3' : 'pb-4'
					}`}
				>
					<h2 className="min-w-0 truncate text-lg font-medium text-[rgb(var(--cg-brand-dark))]">
						{title}
					</h2>
					<AppIconButton
						variant="ghost"
						size="sm"
						aria-label="Close"
						onClick={onClose}
						icon={<XClose className="h-4 w-4" />}
					/>
				</div>
				{tabs && <div className="px-3 connections-sheet-tabs">{tabs}</div>}
			</header>
			<FadingScrollArea
				className={`relative min-h-0 flex-1 overflow-y-auto overflow-x-clip ${
					plain
						? 'bg-[rgb(var(--cg-surface))] p-4'
						: 'bg-[rgb(var(--cg-canvas))] p-2'
				}`}
			>
				{children}
			</FadingScrollArea>
			<footer className="shrink-0 border-t border-[rgb(var(--cg-border))] px-6 py-4">
				{footer}
			</footer>
		</Sheet>,
		document.body
	)
}

export const ConnectionSection = ({
	children,
	className = ''
}: {
	children: ReactNode
	className?: string
}) => (
	<section
		className={`rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))] p-4 ${className}`}
	>
		{children}
	</section>
)

export const ConnectionSectionTitle = ({
	title,
	hint
}: {
	title: string
	hint?: string
}) => (
	<div className="mb-3 last:mb-0">
		<h3 className="text-base font-semibold text-[rgb(var(--cg-text-primary))]">
			{title}
		</h3>
		{hint && (
			<p className="mt-0.5 text-[13px] leading-[18px] text-[rgb(var(--cg-text-secondary))]">
				{hint}
			</p>
		)}
	</div>
)
